import { AIInsight, AIQuestion, Batch, Recipe, Inventory } from '@/types';

const baseUrl =
	(import.meta.env.VITE_AI_API_BASE_URL as string | undefined) ||
	(import.meta.env.VITE_API_BASE_URL as string | undefined) ||
	// 'https://api.beerai.com/dev';
	'https://kht82zjoz6.execute-api.ap-southeast-2.amazonaws.com/dev'

async function http<T>(path: string, options: RequestInit): Promise<T> {
	const res = await fetch(`${baseUrl}${path}`, {
		...options,
		headers: {
			'Content-Type': 'application/json',
			...(options.headers || {})
		}
	});
	if (!res.ok) {
		const contentType = res.headers.get('content-type') || '';
		try {
			if (contentType.includes('application/json')) {
				const data = (await res.json()) as any;
				const msg =
					(typeof data?.message === 'string' && data.message) ||
					(typeof data?.error === 'string' && data.error) ||
					JSON.stringify(data);
				throw new Error(msg || `Request failed: ${res.status}`);
			} else {
				const text = await res.text();
				throw new Error(text || `Request failed: ${res.status}`);
			}
		} catch (e: any) {
			throw new Error(e?.message || `Request failed: ${res.status}`);
		}
	}
	const jsonData = await res.json();
	console.log('http function parsed response:', jsonData);
	console.log('http function response type:', typeof jsonData);
	return jsonData as T;
}

// Mock AI responses for UI development
const generateMockResponse = (question: string, batch: Batch): string => {
	const questionLower = question.toLowerCase();
	
	// Check for common brewing questions
	if (questionLower.includes('gravity') || questionLower.includes('og') || questionLower.includes('fg')) {
		if (batch.originalGravity && batch.finalGravity) {
			const attenuation = ((batch.originalGravity - batch.finalGravity) / (batch.originalGravity - 1)) * 100;
			return `Based on your batch "${batch.name}", your original gravity (OG) was ${batch.originalGravity.toFixed(3)} and final gravity (FG) is ${batch.finalGravity.toFixed(3)}. This gives you an apparent attenuation of approximately ${attenuation.toFixed(1)}%, which is ${attenuation > 75 ? 'excellent' : attenuation > 65 ? 'good' : 'moderate'} for most beer styles.\n\nYour ABV is ${batch.abv?.toFixed(1) || 'calculated'}%, which is within the typical range for this style. The fermentation appears to be progressing well!`;
		}
		return 'I can help you understand gravity readings! Original Gravity (OG) measures the sugar content before fermentation, while Final Gravity (FG) measures it after. The difference helps calculate your ABV.';
	}
	
	if (questionLower.includes('abv') || questionLower.includes('alcohol')) {
		if (batch.abv !== undefined) {
			return `Your batch "${batch.name}" has an ABV of ${batch.abv.toFixed(1)}%. This is calculated from your original gravity of ${batch.originalGravity?.toFixed(3) || 'N/A'} and final gravity of ${batch.finalGravity?.toFixed(3) || 'N/A'}.\n\n${batch.abv > 7 ? 'This is a strong beer - perfect for sipping!' : batch.abv > 5 ? 'This is a moderate-strength beer, great for casual drinking.' : 'This is a session beer, perfect for enjoying multiple servings.'}`;
		}
		return 'ABV (Alcohol By Volume) is calculated from your gravity readings. The formula is: ABV = (OG - FG) × 131.25.';
	}
	
	if (questionLower.includes('ferment') || questionLower.includes('yeast')) {
		return `For your batch "${batch.name}", fermentation is a critical phase. Based on your current status (${batch.status}), here are some tips:\n\n- Maintain consistent temperature (usually 18-22°C for ale yeast)\n- Avoid opening the fermenter unnecessarily to prevent contamination\n- Let fermentation complete fully - this usually takes 1-2 weeks\n- Check gravity readings to confirm fermentation is complete\n\nYour batch appears to be progressing well!`;
	}
	
	if (questionLower.includes('ibu') || questionLower.includes('bitter')) {
		if (batch.ibu !== undefined) {
			return `Your batch "${batch.name}" has an IBU (International Bitterness Units) of ${batch.ibu}. This indicates:\n\n${batch.ibu > 50 ? 'High bitterness - great for IPAs and strong ales' : batch.ibu > 30 ? 'Moderate bitterness - balanced for most styles' : 'Low bitterness - perfect for lighter styles like lagers and wheat beers'}\n\nThe IBU helps balance the malt sweetness with hop bitterness.`;
		}
		return 'IBU measures the bitterness from hops. Higher IBU values mean more bitter beer. Most styles range from 15-80 IBU.';
	}
	
	if (questionLower.includes('color') || questionLower.includes('srm')) {
		if (batch.srm !== undefined) {
			return `Your batch "${batch.name}" has an SRM (Standard Reference Method) color rating of ${batch.srm}. This indicates:\n\n${batch.srm > 20 ? 'Very dark - likely a stout or porter' : batch.srm > 10 ? 'Medium-dark - amber or brown ale' : 'Light color - pale ale, lager, or wheat beer'}\n\nSRM helps you understand the visual appearance and malt character of your beer.`;
		}
		return 'SRM measures beer color. Lower values (1-5) are pale, while higher values (20+) are very dark.';
	}
	
	if (questionLower.includes('when') || questionLower.includes('ready') || questionLower.includes('done')) {
		if (batch.status === 'completed') {
			return `Your batch "${batch.name}" is marked as completed! It should be ready to enjoy. If you bottled it on ${batch.bottlingDate || 'the bottling date'}, make sure it has had time to carbonate (usually 2-3 weeks).\n\nFor best flavor, let it condition for a few more weeks if possible.`;
		}
		if (batch.status === 'fermenting') {
			return `Your batch "${batch.status}" is currently fermenting. It should be ready for bottling/kegging in about 1-2 weeks from the brew date (${batch.brewDate || 'your brew date'}).\n\nCheck your final gravity - when it stays constant for 2-3 days, fermentation is complete!`;
		}
		return 'The timeline depends on your batch status. Fermentation typically takes 1-2 weeks, then conditioning adds another 2-4 weeks for best flavor.';
	}
	
	if (questionLower.includes('improve') || questionLower.includes('better') || questionLower.includes('tip')) {
		return `Here are some tips to improve your batch "${batch.name}":\n\n1. **Temperature Control**: Keep fermentation temperature stable\n2. **Sanitation**: Always sanitize equipment thoroughly\n3. **Yeast Health**: Use fresh yeast and proper pitching rates\n4. **Water Quality**: Good water makes good beer\n5. **Patience**: Let fermentation complete fully before bottling\n\nKeep detailed notes (like you're doing!) to learn from each batch.`;
	}
	
	// Default response
	return `Thanks for asking about your batch "${batch.name}"! I'm here to help with:\n\n- Understanding gravity readings (OG/FG)\n- Calculating ABV\n- Fermentation tips\n- IBU and bitterness\n- Color (SRM)\n- When your beer is ready\n- General brewing advice\n\nWhat would you like to know more about?`;
};

export const AIService = {
	askQuestion: (userId: string, question: string, batchId?: string, recipeId?: string, authHeader?: Record<string, string>) =>
		http<{ answer: string; questionId: string }>(`/ai/${userId}/ask`, {
			method: 'POST',
			body: JSON.stringify({ question, batchId, recipeId }),
			headers: authHeader || {}
		}),

	askQuestionAboutBatch: async (userId: string, question: string, batch: Batch, authHeader?: Record<string, string>): Promise<{ answer: string; questionId: string }> => {
		const result = await http<{ answer: string; questionId: string }>(`/ai/${userId}/ask/batch`, {
			method: 'POST',
			body: JSON.stringify({ question, batch }),
			headers: authHeader || {}
		});
		console.log('AIService response:', result);
		return result;
	},

	askQuestionAboutRecipe: async (userId: string, question: string, recipe: Recipe, authHeader?: Record<string, string>): Promise<{ answer: string; questionId: string }> => {
		const result = await http<{ answer: string; questionId: string }>(`/ai/${userId}/ask/recipe`, {
			method: 'POST',
			body: JSON.stringify({ question, recipe }),
			headers: authHeader || {}
		});
		console.log('AIService response:', result);
		return result;
	},

	askQuestionAboutInventory: async (userId: string, question: string, inventory: Inventory, authHeader?: Record<string, string>): Promise<{ answer: string; questionId: string }> => {
		const result = await http<{ answer: string; questionId: string }>(`/ai/${userId}/ask/inventory`, {
			method: 'POST',
			body: JSON.stringify({ question, inventory }),
			headers: authHeader || {}
		});
		console.log('AIService response:', result);
		return result;
	},

	getInsights: (userId: string, batchId?: string, recipeId?: string, authHeader?: Record<string, string>) =>
		http<{ items: AIInsight[] }>(`/ai/${userId}/insights?${batchId ? `batchId=${batchId}` : ''}${recipeId ? `recipeId=${recipeId}` : ''}`, {
			method: 'GET',
			headers: authHeader || {}
		}),

	analyzeBatch: (userId: string, batchId: string, authHeader: Record<string, string>) =>
		http<{ insights: AIInsight[] }>(`/ai/${userId}/analyze/batch/${batchId}`, {
			method: 'POST',
			headers: authHeader
		}),

	analyzeRecipe: (userId: string, recipeId: string, authHeader: Record<string, string>) =>
		http<{ insights: AIInsight[] }>(`/ai/${userId}/analyze/recipe/${recipeId}`, {
			method: 'POST',
			headers: authHeader
		}),

	getQuestionHistory: (userId: string, authHeader: Record<string, string>, batchId?: string, recipeId?: string, inventoryId?: string) =>
		http<{ items: AIQuestion[] }>(`/ai/${userId}/questions?${batchId ? `batchId=${batchId}` : ''}${recipeId ? `recipeId=${recipeId}` : ''}${inventoryId ? `inventoryId=${inventoryId}` : ''}`, {
			method: 'GET',
			headers: authHeader
		})
};
